import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { validateImageFile } from '@/utils/imageUtils';
import { compressImage } from '@/utils/imageCompression';
import { showToast, showErrorToast, showSuccessToast } from '@/utils/toastUtils';
import { showUploadErrorToast, categorizeUploadError, UploadError } from './utils/errorUtils';

export const useAvatarUpload = (user: User, initialAvatarUrl: string | null, targetUserId?: string) => {
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [processedBlob, setProcessedBlob] = useState<Blob | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(initialAvatarUrl);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [storageError, setStorageError] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [compressionStats, setCompressionStats] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Updated to accept either an event or a direct URL string
  const handleAvatarChange = async (eventOrUrl: React.ChangeEvent<HTMLInputElement> | string, preprocessedBlob?: Blob) => {
    setValidationError(null);
    setCompressionStats(null);
    
    // Handle the case where a direct URL string is provided
    if (typeof eventOrUrl === 'string') {
      console.log("Direct URL provided:", eventOrUrl);
      setAvatarPreview(eventOrUrl);
      return;
    }
    
    // From here, handle the case where an event is provided
    const event = eventOrUrl;
    if (!event.target.files || event.target.files.length === 0) {
      console.log("No file selected");
      return;
    }
    
    const file = event.target.files[0];
    console.log("File selected:", file.name, "Size:", Math.round(file.size / 1024), "KB", "Type:", file.type);
    
    // If we're not provided with a preprocessed blob, validate the image
    if (!preprocessedBlob) {
      try {
        const validation = await validateImageFile(file);
        if (!validation.valid) {
          setValidationError(validation.error);
          toast({
            title: "Image validation failed",
            description: validation.error,
            variant: "destructive",
          });
          return;
        }
        
        // Attempt to compress the avatar if it's not preprocessed already
        try {
          if (file.size > 150 * 1024) { // Only compress if > 150KB
            const compressionOptions = {
              maxWidth: 800, // Avatar doesn't need to be larger
              maxHeight: 800,
              quality: 0.85,
              format: 'webp' as const,
            };
            
            const compressed = await compressImage(file, compressionOptions);
            
            if (compressed.compressedSize < compressed.originalSize) {
              const savedPercentage = Math.round((1 - (compressed.compressedSize / compressed.originalSize)) * 100);
              const stats = `Reduced from ${(compressed.originalSize / 1024).toFixed(0)}KB to ${(compressed.compressedSize / 1024).toFixed(0)}KB (${savedPercentage}% smaller)`;
              console.log('Avatar compression:', stats);
              setCompressionStats(stats);
              preprocessedBlob = compressed.blob;
            }
          }
        } catch (compressErr) {
          console.warn('Avatar compression failed, using original:', compressErr);
          // Continue with original file
        }
      } catch (error) {
        console.error('Image validation error:', error);
      }
    }
    
    setAvatarFile(file);
    
    // If we have a preprocessed blob, use it directly
    if (preprocessedBlob) {
      console.log("Using preprocessed blob:", Math.round(preprocessedBlob.size / 1024), "KB");
      setProcessedBlob(preprocessedBlob);
      const blobUrl = URL.createObjectURL(preprocessedBlob);
      setAvatarPreview(blobUrl);
    } else {
      // Otherwise process the file to generate a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadAvatar = async (): Promise<string | null> => {
    if ((!avatarFile && !processedBlob) || !user) {
      console.log("No file to upload or user not authenticated");
      return initialAvatarUrl;
    }
    
    setIsUploading(true);
    setStorageError(false);
    
    try {
      // Use targetUserId if provided (admin editing), otherwise use current user's ID
      const effectiveUserId = targetUserId || user.id;
      console.log('Starting avatar upload process for user:', effectiveUserId);
      
      // Create a unique filename with user ID as the folder name to match RLS policies
      const fileToUpload = processedBlob || avatarFile;
      if (!fileToUpload) {
        console.log("No file to upload after processing");
        return initialAvatarUrl;
      }
      
      // Determine file extension and content type
      let fileExt: string;
      let contentType: string;
      
      if (fileToUpload instanceof File) {
        fileExt = fileToUpload.name.split('.').pop() || 'jpg';
        contentType = fileToUpload.type;
      } else {
        // For processed blobs, check if it's a GIF
        if (avatarFile && avatarFile.type === 'image/gif') {
          fileExt = 'gif';
          contentType = 'image/gif';
        } else {
          fileExt = 'webp';
          contentType = 'image/webp';
        }
      }
      
      // Format: userId/timestamp.extension (to match our storage policy folder structure)
      const fileName = `${effectiveUserId}/${Date.now()}.${fileExt}`;
      
      console.log('Prepared file name for upload:', fileName, 'to bucket: avatars');
      
      // Upload to the avatars bucket - the RLS policies will check admin status
      const { data, error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, fileToUpload, {
          cacheControl: contentType === 'image/gif' ? 'no-cache' : '3600',
          upsert: true,
          contentType: contentType
        });
      
      if (uploadError) {
        console.error('Upload error:', uploadError);
        
        // Check if this is a storage access error
        if (uploadError.message.includes('not allowed') ||
            uploadError.message.includes('Permission denied') ||
            uploadError.message.includes('access')) {
          
          console.warn('Storage access error. Please check your permissions:', uploadError.message);
          setStorageError(true);
          
          // Try to get more detailed error
          const error = categorizeUploadError(uploadError);
          showUploadErrorToast(error);
          
          // If we have a data URL preview, return it as a fallback
          if (avatarPreview && avatarPreview.startsWith('data:')) {
            return avatarPreview;
          }
          return initialAvatarUrl;
        }
        
        // Use our error handling utility
        const error = categorizeUploadError(uploadError);
        showUploadErrorToast(error);
        throw uploadError;
      }
      
      // Get the public URL
      const { data: publicUrlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);
      
      console.log('Avatar uploaded successfully, public URL:', publicUrlData.publicUrl);
      
      if (compressionStats) {
        toast({
          title: "Success",
          description: `Profile picture ${targetUserId ? "for user" : ""} updated successfully. ${compressionStats}`,
        });
      } else {
        toast({
          title: "Success",
          description: `Profile picture ${targetUserId ? "for user" : ""} updated successfully`,
        });
      }
      
      return publicUrlData.publicUrl;
    } catch (error: any) {
      console.error('Avatar upload failed:', error);
      
      if (error instanceof UploadError) {
        showUploadErrorToast(error);
      } else {
        toast({
          title: "Avatar upload failed",
          description: error.message || "Could not upload image",
          variant: "destructive",
        });
      }
      return initialAvatarUrl;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    avatarFile,
    avatarPreview,
    isUploading,
    storageError,
    validationError,
    compressionStats,
    handleAvatarChange,
    uploadAvatar,
    setAvatarFile,
    setProcessedBlob
  };
};
